import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CalendarDays, ChevronLeft, MapPin, Plus, Sparkles, X } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

type EventRow = {
  id: string;
  name: string;
  event_date: string;
  venue_name: string | null;
  owner?: { id: string; email: string; full_name: string | null } | null;
};

function daysUntil(dateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86400000);
}

function CountdownBadge({ date }: { date: string }) {
  const d = daysUntil(date);
  if (d < 0) return null;
  return (
    <span className="chip bg-gradient-to-br from-brass-100 to-brass-200 text-brass-600 border border-white/60">
      {d === 0 ? 'היום הגדול!' : d === 1 ? 'מחר!' : `עוד ${d} ימים`}
    </span>
  );
}

export default function EventsPage() {
  const { account } = useAuth();
  const isAdmin = account?.account_type === 'admin';
  const [events, setEvents] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [creating, setCreating] = useState(false);

  async function load() {
    try {
      const r = await api.get('/events');
      setEvents(r.data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !date) return;
    setCreating(true);
    try {
      await api.post('/events', { name: name.trim(), event_date: date });
      setName('');
      setDate('');
      setShowAdd(false);
      void load();
      toast.success('האירוע נוצר');
    } catch {
      toast.error('שגיאה ביצירת האירוע');
    } finally {
      setCreating(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-6 page-enter">
        <div className="h-10 w-48 skeleton" />
        <div className="grid gap-3 sm:grid-cols-2">
          {[0, 1].map((i) => (
            <div key={i} className="h-24 skeleton" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 page-enter">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.16em] uppercase text-sage-600 mb-2">
            {isAdmin ? 'Platform' : 'Workspace'}
          </p>
          <h1 className="font-sans text-4xl font-bold tracking-tight heading-accent inline-block pb-1">
            {isAdmin ? 'כל החתונות' : 'האירועים שלי'}
          </h1>
          <p className="text-ink-mute mt-1.5 text-[15px]">
            {isAdmin
              ? 'ניהול כל האירועים במערכת — לחצו לחתונה לניהול מלא'
              : 'כל החתונות במקום אחד — נקי ומדויק'}
          </p>
        </div>
        {!isAdmin && (
          <button type="button" onClick={() => setShowAdd((v) => !v)} className="btn-primary self-start sm:self-auto">
            {showAdd ? <X size={16} /> : <Plus size={16} />}
            {showAdd ? 'סגור' : 'אירוע חדש'}
          </button>
        )}
      </div>

      {showAdd && !isAdmin && (
        <form onSubmit={create} className="surface p-5 sm:p-6 animate-fade-up">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={16} className="text-brass-500" />
            <h2 className="font-semibold text-ink">יצירת אירוע</h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <label htmlFor="ev-name" className="label">שם האירוע</label>
              <input
                id="ev-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="למשל: דנה ויוסי"
                className="field"
                required
              />
            </div>
            <div className="sm:w-48">
              <label htmlFor="ev-date" className="label">תאריך</label>
              <input
                id="ev-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="field"
                required
              />
            </div>
            <div className="flex items-end">
              <button type="submit" disabled={creating} className="btn-secondary w-full sm:w-auto !px-6">
                {creating ? 'שומר…' : 'שמירה'}
              </button>
            </div>
          </div>
        </form>
      )}

      {events.length === 0 ? (
        <div className="surface px-6 py-20 text-center animate-scale-in">
          <div className="icon-tile mx-auto mb-5 w-16 h-16">
            <CalendarDays size={28} strokeWidth={1.75} />
          </div>
          <h3 className="font-display text-2xl font-bold text-ink">עדיין אין אירועים</h3>
          <p className="text-ink-mute mt-2 max-w-sm mx-auto">
            {isAdmin
              ? 'צרו חשבון זוג ואירוע ראשון בעמוד הזוגות.'
              : 'צרו את האירוע הראשון כדי להתחיל לנהל אורחים, טרמפים ומתנות.'}
          </p>
          {!isAdmin && (
            <button type="button" onClick={() => setShowAdd(true)} className="btn-primary mt-6">
              <Plus size={16} /> אירוע חדש
            </button>
          )}
          {isAdmin && (
            <Link to="/platform/couples" className="btn-primary mt-6 inline-flex">
              <Plus size={16} /> יצירת זוג
            </Link>
          )}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {events.map((e, idx) => (
            <Link
              key={e.id}
              to={`/events/${e.id}`}
              className="surface card-3d p-5 flex items-center gap-4 cursor-pointer group animate-scale-in"
              style={{ animationDelay: `${idx * 60}ms` }}
            >
              <div className="icon-tile w-14 h-14">
                <CalendarDays size={24} strokeWidth={1.75} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-ink truncate text-[15px]">{e.name}</span>
                  <CountdownBadge date={e.event_date} />
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-ink-faint">
                  <span className="inline-flex items-center gap-1">
                    <CalendarDays size={12} />
                    {new Date(e.event_date).toLocaleDateString('he-IL', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </span>
                  {e.venue_name && (
                    <span className="inline-flex items-center gap-1 truncate">
                      <MapPin size={12} />
                      {e.venue_name}
                    </span>
                  )}
                </div>
                {isAdmin && e.owner && (
                  <div className="mt-1 text-[12px] text-ink-mute truncate">
                    {e.owner.full_name || e.owner.email}
                  </div>
                )}
              </div>
              <ChevronLeft
                size={18}
                className="text-ink-faint group-hover:text-sage-600 group-hover:-translate-x-0.5 transition-all shrink-0"
              />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
